// Core/internal-api host derivation, reproduced from the sibling homepage-webapp
// (`src/constants/coreHosts.ts` + `src/api/search/searchApiV3.ts::getEbDomain`).
// The `eb-host` request header is only present if the app installs the
// `add_eb_host_header` lambda@edge (memory.md), so we fall back to a per-STAGE host.
//
// SECURITY (SSRF via untrusted eb-host): the `eb-host` request header is
// attacker-controllable, and it is used to build the absolute internal-api URL
// that we then fetch WITH the viewer's cookie forwarded. An unvalidated value
// therefore lets an attacker point the server-side fetch at any host and
// exfiltrate the cookie (SSRF + credential leak). Mitigation mirrors the
// approach shipped in the migrated structured-content-widgets app:
//   - PROD is pinned to the fixed internal host and NEVER honors eb-host;
//   - lower stages honor eb-host only after a strict Eventbrite-domain allowlist
//     (bare hostname, registrable label in {eventbrite,evbqa,evbdev,evbstage}
//     × a known EB TLD; ports/userinfo/paths/lookalikes are rejected).

import type { IncomingHttpHeaders } from 'http';
// NOTE: import from the package's deep subpaths rather than its barrel entry.
// `@eventbrite/webapp-constants@1.0.1`'s published `dist/index.d.ts` re-exports
// from a non-existent `lib/` path, so the barrel (`.../webapp-constants`) has no
// usable type exports (tsc/next-build fail); the `dist/*` modules are intact.
import { EventbriteHeader } from '@eventbrite/webapp-constants/dist/headers';
import { Stage, stageFromString } from '@eventbrite/webapp-constants/dist/Stage';

export const CORE_HOSTS: Record<string, string> = {
    [Stage.LOCAL]: 'localhost',
    [Stage.DEV]: 'evbdev.com',
    [Stage.TEST]: 'evbqa.com',
    [Stage.PROD]: 'eventbrite.com',
};

// The Eventbrite brand labels that may appear as the registrable domain label.
const EB_REGISTRABLE_LABELS = new Set([
    'eventbrite',
    'evbqa',
    'evbdev',
    'evbstage',
]);

// Known Eventbrite public TLDs (from @eventbrite/webapp-locales `SUPPORTED_TLDS`,
// plus the bare `com`). Longer TLDs (e.g. `co.uk`) are matched before their
// shorter suffixes so `eventbrite.co.uk` is not mis-parsed as label `eventbrite.co`.
const KNOWN_EB_TLDS = [
    'co.nz',
    'co.uk',
    'com.ar',
    'com.au',
    'com.br',
    'com.mx',
    'com.pe',
    'com',
    'at',
    'be',
    'ca',
    'ch',
    'cl',
    'co',
    'de',
    'dk',
    'es',
    'fi',
    'fr',
    'hk',
    'ie',
    'in',
    'it',
    'nl',
    'no',
    'pt',
    'se',
    'sg',
].sort((a, b) => b.length - a.length);

/**
 * Strictly validate an `eb-host` header value against the Eventbrite domain
 * allowlist. Accepts ONLY a bare hostname whose registrable domain is
 * `<eb-brand>.<known-eb-tld>` (an optional leading `www.` is allowed). Rejects
 * anything carrying a scheme, userinfo (`@`), port (`:`), path/query/fragment,
 * backslashes, whitespace, control chars, or any lookalike host
 * (`eventbrite.evil.com`, `eventbrite.com.evil.com`, raw IPs, etc.).
 */
export function isAllowedEbHost(rawHost: string | undefined | null): boolean {
    if (!rawHost || typeof rawHost !== 'string') {
        return false;
    }

    // A legitimate hostname contains only letters, digits, dots and hyphens.
    // This single check rejects scheme (`//`, `:`), userinfo (`@`), ports (`:`),
    // paths/queries/fragments (`/?#`), backslashes and control chars in one shot.
    if (/[^a-zA-Z0-9.-]/.test(rawHost)) {
        return false;
    }
    if (
        rawHost.includes('..') ||
        rawHost.startsWith('.') ||
        rawHost.endsWith('.') ||
        rawHost.startsWith('-')
    ) {
        return false;
    }

    const host = rawHost.toLowerCase().replace(/^www\./, '');

    for (const tld of KNOWN_EB_TLDS) {
        const suffix = `.${tld}`;
        if (host.endsWith(suffix)) {
            const label = host.slice(0, host.length - suffix.length);
            // The registrable label must be EXACTLY an EB brand — no leftover
            // subdomains and no lookalike labels.
            if (EB_REGISTRABLE_LABELS.has(label)) {
                return true;
            }
        }
    }

    return false;
}

export function getStage(): Stage {
    return stageFromString(process.env.STAGE || Stage.TEST);
}

/** The Eventbrite domain (no `www.`) to build absolute Core/internal-api URLs. */
export function getEbDomain(req?: { headers: IncomingHttpHeaders }): string {
    const stage = getStage();

    // PROD: never trust a client-supplied host. Pin to the fixed internal host
    // so the server-side fetch (which forwards the viewer's cookie) can only
    // ever reach `internal-api.eventbrite.com`.
    if (stage === Stage.PROD) {
        return CORE_HOSTS[Stage.PROD];
    }

    // Lower stages: honor `eb-host` ONLY after strict Eventbrite-domain
    // allowlisting; otherwise fall back to the per-stage default.
    const ebHost = req?.headers[EventbriteHeader.EB_HOST] as string | undefined;
    if (ebHost && isAllowedEbHost(ebHost)) {
        return ebHost.replace(/^www\./, '');
    }

    return CORE_HOSTS[stage];
}

/**
 * The Core host subdomain for SERVER-side fetches: `internal-api` is an
 * in-network NLB that exists ONLY for prod (`internal-api.eventbrite.com`).
 * Lower stages (DEV/TEST) have no internal-api NLB and must reach Core via the
 * public `www` host (`www.evbdev.com` / `www.evbqa.com`) — same as the sibling
 * homepage `searchApiV3.ts` and structured-content-widgets bridges
 * (`internal-api` prod / `www` lower). Mirror that by keying off STAGE, not
 * NODE_ENV (in a Lambda NODE_ENV is always `production`, so the old check
 * routed DEV/TEST to a nonexistent `internal-api.evbdev.com` → ENOTFOUND).
 */
export function getCoreApiSubdomain(stage?: Stage): string {
    const s = stage ?? getStage();
    return s === Stage.PROD ? 'internal-api' : 'www';
}
